/**
 * Video Generation API Client
 * 
 * Provides functions to generate videos using the direct API endpoint
 * that bypasses ADK function calling to avoid token limit issues.
 * 
 * Note: Video generation is asynchronous and may take 2-3 minutes.
 */

import config from './config';
import { getBeHeaders } from './utils/apiHeaders';
import { getAuthorizedFetch } from './utils/fetchRegistry';

/**
 * Generate video using the direct API endpoint with veo-2.0-generate-001 model.
 * 
 * @param {string} prompt - Text description of the video to generate
 * @param {string} aspectRatio - Aspect ratio (16:9, 9:16, 16:10, 1:1)
 * @param {number} durationSeconds - Video duration in seconds (5-8, will be clamped)
 * @returns {Promise<Object>} Response with generated video or error
 */
export async function generateVideo(prompt, aspectRatio = '16:9', durationSeconds = 5) {
    const baseUrl = config.agentUrl || 'http://localhost:8000';
    
    try {
        // Video generation can take 2-3 minutes, so increase timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 240000); // 4 minute timeout
        
        const response = await getAuthorizedFetch()(`${baseUrl}/api/generate-video`, {
            method: 'POST',
            headers: getBeHeaders({
                'Content-Type': 'application/json',
            }),
            body: JSON.stringify({
                prompt,
                aspect_ratio: aspectRatio,
                duration_seconds: durationSeconds,
            }),
            signal: controller.signal,
        });
        
        clearTimeout(timeoutId);
        
        if (!response.ok) {
            const errorData = await response.json();
            return {
                success: false,
                message: errorData.detail || 'Failed to generate video',
            };
        }
        
        const data = await response.json();
        
        return {
            success: true,
            data: data,
        };
    } catch (error) {
        if (error.name === 'AbortError') {
            return {
                success: false,
                message: 'Video generation timed out. Please try again with a shorter duration or simpler prompt.',
            };
        }
        return {
            success: false,
            message: error.message || 'Network error while generating video',
        };
    }
}

/**
 * Extract video generation request from user message.
 * Detects if the user is asking to generate a video.
 * 
 * @param {string} message - User's message
 * @returns {Object|null} Video generation params or null if not a video request
 */
export function detectVideoGenerationRequest(message) {
    const lowerMessage = message.toLowerCase();
    
    // Check for video generation keywords
    const videoKeywords = [
        'generate video',
        'generate a video',
        'create video',
        'create a video',
        'make video',
        'make a video',
        'produce video',
        'produce a video',
    ];
    
    const isVideoRequest = videoKeywords.some(keyword => lowerMessage.includes(keyword));
    
    if (!isVideoRequest) {
        return null;
    }
    
    // Extract prompt (text after the command)
    let prompt = message;
    for (const keyword of videoKeywords) {
        const regex = new RegExp(keyword + '\\s*(of|:)?\\s*', 'i');
        prompt = prompt.replace(regex, '').trim();
    }
    
    // Extract aspect ratio if mentioned
    let aspectRatio = '16:9';
    const ratioPatterns = [
        /\b(16:9|16x9|landscape|wide|horizontal)\b/i,
        /\b(9:16|9x16|portrait|tall|vertical)\b/i,
        /\b(1:1|1x1|square)\b/i,
    ];
    
    const ratioMap = {
        '16:9': '16:9', '16x9': '16:9', 'landscape': '16:9', 'wide': '16:9', 'horizontal': '16:9',
        '9:16': '9:16', '9x16': '9:16', 'portrait': '9:16', 'tall': '9:16', 'vertical': '9:16',
        '1:1': '1:1', '1x1': '1:1', 'square': '1:1',
    };
    
    for (const pattern of ratioPatterns) {
        const match = message.match(pattern);
        if (match) {
            aspectRatio = ratioMap[match[1].toLowerCase()] || '16:9';
            // Remove aspect ratio from prompt
            prompt = prompt.replace(pattern, '').trim();
            break;
        }
    }
    
    // Extract duration if mentioned
    let durationSeconds = 5;
    const durationPattern = /\b(\d+)\s*(second|sec|s)\b/i;
    const durationMatch = message.match(durationPattern);
    if (durationMatch) {
        const duration = parseInt(durationMatch[1], 10);
        if (duration >= 1 && duration <= 10) {
            durationSeconds = duration;
            // Remove duration specification from prompt
            prompt = prompt.replace(durationPattern, '').trim();
        }
    }
    
    // Clean up prompt
    prompt = prompt.replace(/\s+/g, ' ').trim();
    
    if (!prompt) {
        return null; // No actual prompt provided
    }
    
    return {
        prompt,
        aspectRatio,
        durationSeconds,
    };
}
